// web/src/api/stripe.ts
// Web client wrapper for the three Stripe routes exposed by the lambda:
//
//   POST /api/v1/stripe/checkout  — body { plan }            → { url }
//   POST /api/v1/stripe/portal    — no body                   → { url }
//   GET  /api/v1/stripe/status    — no body                   → { plan, subscriptionStatus, currentPeriodEnd, gamesUsedThisMonth }
//
// Each call is authenticated by the parent's id token (handled by useApi).
// Checkout + portal both return a Stripe-hosted URL; the caller is
// responsible for `window.location.href = url` (the lambda never redirects).

import { useMemo } from 'react';
import { useApi } from './useApi';
import type { Plan, SubscriptionStatus } from '../types';

export interface StripeStatus {
  plan: Plan;
  subscriptionStatus: SubscriptionStatus | 'inactive';
  currentPeriodEnd: string | null;
  gamesUsedThisMonth: number;
}

export function useStripeApi() {
  const call = useApi();
  return useMemo(() => ({
    /**
     * Creates a Stripe Checkout session for the given plan. Returns the URL —
     * caller redirects the browser to it. Stripe handles the rest; webhook
     * flips the user's plan in DDB on completion.
     */
    checkout: (plan: 'stats' | 'reports') =>
      call<{ url: string; trial?: boolean }>('/api/v1/stripe/checkout', {
        method: 'POST',
        body: { plan },
      }),
    /**
     * Returns a Stripe Customer Portal URL for managing the existing
     * subscription (change plan, cancel, update card). Only valid if the
     * caller already has a stripeCustomerId — server returns 400 otherwise.
     */
    portal: () =>
      call<{ url: string }>('/api/v1/stripe/portal', { method: 'POST', body: {} }),
    /** Current subscription state — used to show plan + status on the dashboard. */
    status: () => call<StripeStatus>('/api/v1/stripe/status'),
  }), [call]);
}
